import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ModuleConsoleFooter } from "@/components/composition/ModuleConsoleFooter";
import { PageHeader } from "@/components/PageHeader";
import {
  type ProductLabelData,
} from "@/components/supply/ProductLabelPrintSheet";
import { LabelPrintSettingsPanel } from "@/components/printing/LabelPrintSettingsPanel";
import { LabelPrintPreview } from "@/components/supply/LabelPrintPreview";
import {
  getLabelPrintFormat,
  labelPrintSizeHint,
} from "@/core/printing/label-print-formats";
import {
  loadLabelPrintUserSettings,
  type LabelPrintUserSettings,
} from "@/core/printing/label-print-settings";
import { t } from "@/core/composition/lexicon";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import { IscApiError } from "@/core/inventory/isc-client";
import { persistProductPrice } from "@/core/inventory/pharmacy-stock-sync";
import {
  depotSupplyRoutes,
  STOCK_DEPOTS,
  type StockDepotId,
} from "@/core/inventory/stock-depots";
import { isCode39Compatible } from "@/core/printing/code39-barcode";
import type { StockProduct } from "@/core/pharmacy/types";
import { routes } from "@/router/all_routes";

type ReceiptMode = "existing" | "new";

function isApiOfflineError(err: unknown): boolean {
  return err instanceof IscApiError && err.status === 0;
}

function generateInternalBarcode(): string {
  return `HOS${Date.now().toString(36).toUpperCase()}${Math.random().toString(36).slice(2, 5).toUpperCase()}`;
}

export function SupplyReceiptsPage({ depot }: { depot: StockDepotId }) {
  const depotConfig = STOCK_DEPOTS[depot];
  const depotRoutes = depotSupplyRoutes(depot);
  const [products, setProducts] = useState<StockProduct[]>([]);
  const [mode, setMode] = useState<ReceiptMode>("existing");
  const [productId, setProductId] = useState("");
  const [newName, setNewName] = useState("");
  const [newBarcode, setNewBarcode] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unitId, setUnitId] = useState("");
  const [storeId, setStoreId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [labelPrinted, setLabelPrinted] = useState(false);
  const [printSettings, setPrintSettings] = useState<LabelPrintUserSettings>(() =>
    loadLabelPrintUserSettings(),
  );
  const labelFormat = useMemo(
    () => getLabelPrintFormat(printSettings.formatId) ?? getLabelPrintFormat("60x40-180")!,
    [printSettings.formatId],
  );
  const labelRotation = printSettings.rotation;
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const resetWorkflow = useCallback(() => {
    setLabelPrinted(false);
    setSuccess(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, store, units] = await Promise.all([
        pharmacyInventoryApi.listProductsForDepot(depot),
        pharmacyInventoryApi.getDepotStoreId(depot),
        pharmacyInventoryApi.listUnits(),
      ]);
      const active = list.filter((p) => p.status === "ACTIVE").sort((a, b) =>
        a.label.localeCompare(b.label, "fr"),
      );
      setProducts(active);
      setStoreId(store);
      setProductId((prev) => prev || active[0]?.id || "");
      const activeUnit = units.find((u) => u.isActive) ?? units[0];
      if (activeUnit) setUnitId(activeUnit.id);
    } catch (err) {
      setError(
        isApiOfflineError(err)
          ? "API stock inaccessible."
          : err instanceof Error
            ? err.message
            : "Chargement impossible.",
      );
    } finally {
      setLoading(false);
    }
  }, [depot]);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = useMemo(
    () => products.find((p) => p.id === productId) ?? null,
    [products, productId],
  );

  const qtyNum = Number(quantity);

  const labelDraft = useMemo((): ProductLabelData | null => {
    if (qtyNum <= 0) return null;
    if (mode === "existing") {
      if (!selected) return null;
      const barcode = (selected.barcode ?? selected.sku).trim();
      if (!barcode) return null;
      return {
        productName: selected.label,
        barcode,
        sku: selected.sku,
        quantity: qtyNum,
        unit: selected.unit,
      };
    }
    const name = newName.trim();
    const barcode = newBarcode.trim();
    if (!name || !barcode) return null;
    return {
      productName: name,
      barcode,
      sku: barcode,
      quantity: qtyNum,
    };
  }, [mode, selected, newName, newBarcode, qtyNum]);

  const canPrintLabel =
    Boolean(labelDraft) &&
    isCode39Compatible(labelDraft?.barcode ?? "") &&
    !loading &&
    Boolean(storeId);

  const canStockIn =
    labelPrinted &&
    canPrintLabel &&
    !submitting &&
    (mode === "existing" ? Boolean(productId) : Boolean(newName.trim() && unitId));

  const handleModeChange = (next: ReceiptMode) => {
    setMode(next);
    resetWorkflow();
    if (next === "new" && !newBarcode) {
      setNewBarcode(generateInternalBarcode());
    }
  };

  const handlePrintSettingsChange = (settings: LabelPrintUserSettings) => {
    setPrintSettings(settings);
    setLabelPrinted(false);
  };

  const handleStockIn = async (event: FormEvent) => {
    event.preventDefault();
    if (!canStockIn || !storeId || !labelDraft) {
      setError("Imprimez d'abord l'étiquette, puis validez l'entrée en stock.");
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (mode === "existing") {
        if (!productId) throw new Error("Article requis.");
        await pharmacyInventoryApi.postReceipt(
          productId,
          storeId,
          qtyNum,
          depotConfig.receiptReference,
        );
        setSuccess(
          `Entrée en stock — ${qtyNum} × ${selected?.label ?? "article"} (étiquette posée, mouvement RECEIPT).`,
        );
      } else {
        const name = newName.trim();
        const code = newBarcode.replace(/[^a-zA-Z0-9-]/g, "").slice(0, 40).toUpperCase()
          || generateInternalBarcode();
        const priceNum = Number(newPrice);
        const product = await pharmacyInventoryApi.createProduct({
          code,
          sku: code,
          name,
          barcode: newBarcode.trim() || undefined,
          unitId,
          sellingPrice: priceNum > 0 ? priceNum : undefined,
        });
        await pharmacyInventoryApi.postReceipt(
          product.id,
          storeId,
          qtyNum,
          depotConfig.receiptReference,
        );
        if (priceNum > 0) {
          await persistProductPrice(product.id, priceNum);
        }
        setProducts((prev) =>
          [...prev, product].sort((a, b) => a.label.localeCompare(b.label, "fr")),
        );
        setSuccess(
          `Nouveau produit créé et stocké — ${qtyNum} × ${name} (code ${code}).`,
        );
        setNewName("");
        setNewBarcode(generateInternalBarcode());
        setNewPrice("");
        setMode("existing");
        setProductId(product.id);
      }
      setQuantity("1");
      setLabelPrinted(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Entrée en stock impossible");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ph-dashboard">
      <PageHeader
        title={`${depotConfig.label} — ${t("supply.receipts")}`}
        description={`Réception dépôt ${depotConfig.storeCode} — étiquette code-barres obligatoire avant l'entrée en stock ISC.`}
        actions={
          <div className="d-flex gap-2 ph-page-header-actions">
            <Link to={depotRoutes.receiptsHistory} className="btn btn-outline-secondary btn-sm">
              Historique réceptions
            </Link>
            <Link to={depotRoutes.articles} className="btn btn-outline-secondary btn-sm">
              {t("supply.articles")}
            </Link>
          </div>
        }
      />

      <div className="row g-3 ph-split-workspace">
        <div className="col-lg-5">
          <div className="ph-dash-card">
            <h3 className="h6 mb-3">Nouvelle réception</h3>

            <div className="btn-group btn-group-sm w-100 mb-3" role="group" aria-label="Type de réception">
              <button
                type="button"
                className={`btn ${mode === "existing" ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => handleModeChange("existing")}
              >
                Article existant
              </button>
              <button
                type="button"
                className={`btn ${mode === "new" ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => handleModeChange("new")}
              >
                Nouveau produit
              </button>
            </div>

            <form onSubmit={handleStockIn} className="d-flex flex-column gap-3">
              {mode === "existing" ? (
                <div>
                  <label className="form-label small fw-semibold" htmlFor="receipt-product">
                    {t("inventory.article")}
                  </label>
                  <select
                    id="receipt-product"
                    className="form-select form-select-sm"
                    value={productId}
                    onChange={(e) => {
                      setProductId(e.target.value);
                      resetWorkflow();
                    }}
                    disabled={loading || products.length === 0}
                  >
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label} ({p.sku})
                      </option>
                    ))}
                  </select>
                  {selected && (
                    <p className="small text-muted mb-0 mt-1">
                      Code-barres : {selected.barcode ?? selected.sku}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <label className="form-label small fw-semibold" htmlFor="receipt-new-name">
                      Nom du produit
                    </label>
                    <input
                      id="receipt-new-name"
                      className="form-control form-control-sm"
                      value={newName}
                      onChange={(e) => {
                        setNewName(e.target.value);
                        resetWorkflow();
                      }}
                      placeholder="Ex. Paracétamol 500 mg"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label small fw-semibold" htmlFor="receipt-new-barcode">
                      Code-barres
                    </label>
                    <div className="input-group input-group-sm">
                      <input
                        id="receipt-new-barcode"
                        className="form-control"
                        value={newBarcode}
                        onChange={(e) => {
                          setNewBarcode(e.target.value);
                          resetWorkflow();
                        }}
                        placeholder="Scan ou saisie manuelle"
                        required
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          setNewBarcode(generateInternalBarcode());
                          resetWorkflow();
                        }}
                      >
                        Générer
                      </button>
                    </div>
                    {newBarcode && !isCode39Compatible(newBarcode) && (
                      <p className="small text-warning mb-0 mt-1">
                        Caractères autorisés : A-Z, 0-9, - . espace
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="form-label small fw-semibold" htmlFor="receipt-new-price">
                      Prix unitaire (optionnel)
                    </label>
                    <input
                      id="receipt-new-price"
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control form-control-sm"
                      value={newPrice}
                      onChange={(e) => setNewPrice(e.target.value)}
                    />
                  </div>
                </>
              )}

              <div>
                <label className="form-label small fw-semibold" htmlFor="receipt-qty">
                  Quantité reçue
                </label>
                <input
                  id="receipt-qty"
                  type="number"
                  min="1"
                  step="1"
                  className="form-control form-control-sm"
                  value={quantity}
                  onChange={(e) => {
                    setQuantity(e.target.value);
                    resetWorkflow();
                  }}
                />
              </div>

              {error && <div className="alert alert-warning py-2 mb-0 small">{error}</div>}
              {success && <div className="alert alert-success py-2 mb-0 small">{success}</div>}

              <div className="d-flex flex-column gap-2">
                {canPrintLabel && (
                  <LabelPrintSettingsPanel
                    label={labelDraft}
                    disabled={!canPrintLabel}
                    compact
                    onPrinted={() => setLabelPrinted(true)}
                    onError={setError}
                    onSuccess={setSuccess}
                    onSettingsChange={handlePrintSettingsChange}
                  />
                )}
                <button
                  type="submit"
                  className="btn btn-success btn-sm"
                  disabled={!canStockIn}
                  title={
                    labelPrinted
                      ? undefined
                      : "Imprimez l'étiquette avant l'entrée en stock"
                  }
                >
                  {submitting ? "Enregistrement…" : t("action.labelAndStockIn")}
                </button>
              </div>

              {!labelPrinted && canPrintLabel && (
                <p className="small text-muted mb-0">
                  1. Imprimez et posez l'étiquette sur le produit.
                  <br />
                  2. Puis cliquez « {t("action.labelAndStockIn")} ».
                </p>
              )}
              {labelPrinted && (
                <p className="small text-success mb-0">
                  Étiquette imprimée — vous pouvez valider l'entrée en stock.
                </p>
              )}
              {!storeId && (
                <p className="small text-muted mb-0">Aucun entrepôt ISC configuré.</p>
              )}
            </form>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="ph-dash-card h-100">
            <h3 className="h6 mb-2">Aperçu étiquette</h3>
            {labelDraft && isCode39Compatible(labelDraft.barcode) ? (
              <>
                <LabelPrintPreview
                  label={labelDraft}
                  format={labelFormat}
                  rotation={labelRotation}
                />
                <p className="ph-label-print-hint mb-0">
                  {labelPrintSizeHint(labelFormat, labelRotation)}. Impression via{" "}
                  <strong>Print Bridge</strong> (TSPL direct) — format et rotation appliqués sans
                  dialogue Chrome.
                </p>
              </>
            ) : (
              <p className="small text-muted mb-3">
                Renseignez l'article et la quantité pour prévisualiser l'étiquette.
              </p>
            )}
            <h3 className="h6 mb-2 mt-4">Parcours réception</h3>
            <ul className="small text-muted mb-0 ps-3">
              <li>Article existant ou création d'un nouveau produit sur place.</li>
              <li>Impression du code-barres avant toute entrée en stock.</li>
              <li>Entrée ISC (mouvement RECEIPT) après étiquetage.</li>
              <li>Les niveaux se mettent à jour sur {t("supply.levels").toLowerCase()}.</li>
            </ul>
          </div>
        </div>
      </div>

      <ModuleConsoleFooter consoleRoute={routes.supplyAdminConsole} moduleLabel="ITaaS Inventory" />
    </div>
  );
}

export function SupplyMedicinesReceiptsPage() {
  return <SupplyReceiptsPage depot="pharmacy" />;
}

export function SupplyConsumablesReceiptsPage() {
  return <SupplyReceiptsPage depot="consumables" />;
}
